import React, { useMemo, useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { geoMercator } from 'd3-geo';
import {
  Building2, MapPin, Crosshair, Radar, Settings, Star,
} from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { useTranslation } from '../../i18n';
import { MAP_CITIES, findCityById, haversineKm } from '../../data/mapCities';
import {
  getDepotCityId, getDepotRadiusKm, getDepotRent, getCityBaseRent,
  evaluateContractCoverage, COMPETITOR_HOME_CITIES, DEPOT_BASE_RADIUS_KM,
  OPEN_DEPOT_SUBSIDY_RATE, MAX_INTERNATIONAL_DEPOTS,
} from '../../data/depotGeo';
import { MAX_OPENED_DEPOTS, splitDepots, getDepotOccupancy } from '../../data/depotEconomics';
import { ACQUISITION_CONSTANTS } from '../../store/slices/acquisitionSlice';
import { COMPETITORS } from '../../data/competitors';
// 11/02/2026 — Section « Évolutions possibles » retirée de la carte
// stratégique (retour user prod : gérées individuellement dans chaque
// onglet dépôt). Les imports DepotUpgradesList / HqZoneUpgrades ne sont
// plus nécessaires ici.
import ProspectPanel from './ProspectPanel';
import NewDepotModal from './NewDepotModal';
import { formatMoney } from '../../utils/format';

const GEO_URL = '/geo/europe.topojson';
const W = 800;
const H = 460;

const DEPOT_COLORS = {
  hq: '#F59E0B',        // QG — ambre
  opened: '#34D399',    // satellite — émeraude
  acquired: '#A78BFA',  // racheté — violet
};
const PROSPECT_COLOR = '#22D3EE'; // cyan — villes libres / prospection

const depotKind = (d) => (d.isHeadquarters ? 'hq' : d.acquired ? 'acquired' : 'opened');

/**
 * DepotNetworkMap — vue carte à l'entrée de l'onglet Dépôt.
 *
 * 14/06/2026 — Interactions étendues :
 *   - Dépôts possédés cliquables → panneau stats + évolutions DÉTAILLÉES
 *     (atouts + conditions obligatoires au clic, QG inclus avec la pompe).
 *   - Villes LIBRES (◇) : survol → aperçu du rayon 250 km ; clic → panneau
 *     « prospection » (loyer, contrats débloqués) + ouverture directe d'un
 *     dépôt via NewDepotModal.
 *   - Bouton « Gérer ce dépôt » : bascule le dépôt actif ET scrolle vers la
 *     zone de gestion (fix : aucune action visible auparavant).
 */
export default function DepotNetworkMap() {
  const { t } = useTranslation();
  const ownedDepots = useGameStore(s => s.ownedDepots || EMPTY_ARRAY);
  const company = useGameStore(s => s.company);
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const phase = useGameStore(s => s.phase || 1);
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const activeContracts = useGameStore(s => s.activeContracts || EMPTY_ARRAY);
  const availableContracts = useGameStore(s => s.availableContracts || EMPTY_ARRAY);
  const acquiredCompetitorIds = useGameStore(s => s.acquiredCompetitorIds || EMPTY_ARRAY);
  const switchActiveDepot = useGameStore(s => s.switchActiveDepot);

  // Sélection : un dépôt possédé OU une ville libre (prospection).
  const [selection, setSelection] = useState(() => ({ type: 'depot', id: ownedDepots[0]?.id || null }));
  const [hoverCityId, setHoverCityId] = useState(null);
  const [modalCityId, setModalCityId] = useState(null);
  const setSelectedId = (id) => setSelection({ type: 'depot', id });
  const selected = selection.type === 'depot'
    ? (ownedDepots.find(d => d.id === selection.id) || ownedDepots[0] || null)
    : null;

  // Points du réseau : dépôts possédés (avec ville résolue).
  const depotPoints = useMemo(
    () => ownedDepots
      .map(d => ({ depot: d, city: findCityById(getDepotCityId(d, company)) }))
      .filter(p => p.city),
    [ownedDepots, company],
  );
  const occupiedCityIds = useMemo(
    () => new Set(depotPoints.map(p => p.city.id)),
    [depotPoints],
  );

  // Quotas d'ouverture (3 créés FR + 2 internationaux).
  const quotas = useMemo(() => {
    const { opened } = splitDepots(ownedDepots);
    const isIntl = (d) => {
      const c = findCityById(getDepotCityId(d, company));
      return c && c.country !== 'FR';
    };
    return {
      openedFrFull: opened.filter(d => !isIntl(d)).length >= MAX_OPENED_DEPOTS,
      intlFull: ownedDepots.filter(isIntl).length >= MAX_INTERNATIONAL_DEPOTS,
    };
  }, [ownedDepots, company]);

  // Villes libres (prospection) : toutes les villes sans dépôt à nous.
  const candidates = useMemo(
    () => MAP_CITIES
      .filter(c => !occupiedCityIds.has(c.id))
      .map(c => {
        let status = 'open';
        // 10/07/2026 — dès la phase 3, les villes étrangères (phase 4) sont
        // ouvrables : c'est l'action qui déclenche le passage en phase 4.
        if (c.phase > (phase >= 3 ? 4 : phase)) status = 'locked';
        else if (c.country !== 'FR' && quotas.intlFull) status = 'quota_intl';
        else if (c.country === 'FR' && quotas.openedFrFull) status = 'quota_fr';
        return { city: c, status };
      }),
    [occupiedCityIds, phase, quotas],
  );

  // Quand un dépôt vient d'être ouvert sur la ville prospectée → bascule la
  // sélection sur le nouveau dépôt (panneau stats + évolutions).
  useEffect(() => {
    if (selection.type === 'prospect' && occupiedCityIds.has(selection.id)) {
      const p = depotPoints.find(pt => pt.city.id === selection.id);
      if (p) setSelection({ type: 'depot', id: p.depot.id });
    }
  }, [selection, occupiedCityIds, depotPoints]);

  // Cibles M&A : concurrents pas encore rachetés, ancrés dans leur ville.
  const targets = useMemo(
    () => COMPETITORS
      .filter(c => !acquiredCompetitorIds.includes(c.id) && COMPETITOR_HOME_CITIES[c.id])
      .map(c => ({ competitor: c, city: findCityById(COMPETITOR_HOME_CITIES[c.id]) }))
      .filter(x => x.city),
    [acquiredCompetitorIds],
  );

  // Origines des contrats disponibles, agrégées par ville (vert = couvert).
  const contractDots = useMemo(() => {
    const byCity = {};
    availableContracts.forEach(c => {
      if (!c.originCityId) return;
      const cov = evaluateContractCoverage(c, ownedDepots, company, activeInvestments);
      if (!byCity[c.originCityId]) {
        byCity[c.originCityId] = { city: findCityById(c.originCityId), count: 0, covered: cov.covered };
      }
      byCity[c.originCityId].count += 1;
      if (!cov.covered) byCity[c.originCityId].covered = false;
    });
    return Object.values(byCity).filter(x => x.city);
  }, [availableContracts, ownedDepots, company, activeInvestments]);

  // Vue : zoom France, élargi si une implantation/UE est hors hexagone.
  const hasIntl = depotPoints.some(p => p.city.country !== 'FR');
  const center = hasIntl ? [6.5, 47.0] : [2.6, 46.6];
  const scale = hasIntl ? 800 : 1350;

  // Projection IDENTIQUE à celle de ComposableMap (même center/scale/translate)
  // → permet de convertir un rayon km en pixels.
  const projection = useMemo(
    () => geoMercator().center(center).scale(scale).translate([W / 2, H / 2]),
    [center[0], center[1], scale], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const kmToPx = (city, radiusKm) => {
    const lngOffset = radiusKm / (111.32 * Math.cos((city.lat * Math.PI) / 180));
    const p0 = projection([city.lng, city.lat]);
    const p1 = projection([city.lng + lngOffset, city.lat]);
    if (!p0 || !p1) return null;
    return Math.abs(p1[0] - p0[0]);
  };

  // Cercle du dépôt sélectionné.
  const radiusCircle = useMemo(() => {
    if (!selected) return null;
    const city = findCityById(getDepotCityId(selected, company));
    if (!city) return null;
    const radiusKm = getDepotRadiusKm(selected, activeInvestments);
    const px = kmToPx(city, radiusKm);
    if (px == null) return null;
    return { center: [city.lng, city.lat], px, radiusKm };
  }, [selected, company, projection, activeInvestments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aperçu prospection : ville survolée OU ville sélectionnée en prospection.
  const previewCity = useMemo(() => {
    if (hoverCityId) return findCityById(hoverCityId);
    if (selection.type === 'prospect') return findCityById(selection.id);
    return null;
  }, [hoverCityId, selection]);

  const previewCircle = useMemo(() => {
    if (!previewCity) return null;
    const px = kmToPx(previewCity, DEPOT_BASE_RADIUS_KM);
    if (px == null) return null;
    return { center: [previewCity.lng, previewCity.lat], px };
  }, [previewCity, projection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contrats locaux qui seraient débloqués par une implantation dans `city`.
  const computeUnlocked = (city) => {
    let inRange = 0;
    let unlocked = 0;
    availableContracts.forEach(c => {
      if (!c.originCityId) return;
      const origin = findCityById(c.originCityId);
      if (!origin || haversineKm(city, origin) > DEPOT_BASE_RADIUS_KM) return;
      inRange += 1;
      if (!evaluateContractCoverage(c, ownedDepots, company, activeInvestments).covered) unlocked += 1;
    });
    return { inRange, unlocked };
  };

  const hoverStats = useMemo(
    () => (hoverCityId ? computeUnlocked(findCityById(hoverCityId)) : null),
    [hoverCityId, availableContracts, ownedDepots, company], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Stats complètes pour le panneau prospection.
  const prospectStats = useMemo(() => {
    if (selection.type !== 'prospect') return null;
    const entry = candidates.find(x => x.city.id === selection.id);
    if (!entry) return null;
    const { city, status } = entry;
    const grossCost = ACQUISITION_CONSTANTS.NEW_DEPOT_OPEN_COST;
    const netCost = grossCost - Math.round(grossCost * OPEN_DEPOT_SUBSIDY_RATE);
    const { inRange, unlocked } = computeUnlocked(city);
    return {
      city,
      status,
      radiusKm: DEPOT_BASE_RADIUS_KM,
      rentEstimate: Math.round(getCityBaseRent(city.id) * 0.8 * (city.country !== 'FR' ? 1.25 : 1)),
      inRange,
      unlocked,
      netCost,
    };
  }, [selection, candidates, availableContracts, ownedDepots, company]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats du dépôt sélectionné pour le panneau latéral.
  const panelStats = useMemo(() => {
    if (!selected) return null;
    const cityId = getDepotCityId(selected, company);
    const city = findCityById(cityId);
    const isDefaultDepot = selected.isHeadquarters;
    // 17/06/2026 — Affichage utilise getDepotOccupancy : véhicules sur
    // contrat régulier exclus du compteur (en mission longue, roulants).
    const vCount = getDepotOccupancy(selected.id, vehicles, activeContracts);
    const dCount = drivers.filter(d => (d.depotId || 'depot-hq') === selected.id || (isDefaultDepot && !d.depotId)).length;
    const served = activeContracts.filter(c => (c.servingDepotId || 'depot-hq') === selected.id || (isDefaultDepot && !c.servingDepotId)).length;
    return {
      city,
      rent: getDepotRent(selected, activeInvestments, company, totalDays),
      radiusKm: getDepotRadiusKm(selected, activeInvestments),
      vCount,
      dCount,
      served,
    };
  }, [selected, company, vehicles, drivers, activeContracts, activeInvestments, totalDays]);

  // 08/02/2026 — Q2 Phase 3 refonte : « Gérer ce dépôt » dispatch un
  // évènement personnalisé que Depot.jsx écoute pour basculer la vue
  // active vers l'onglet du dépôt. On garde aussi le scroll vers la
  // zone de management pour fluidifier la navigation.
  const handleManage = (depotId) => {
    switchActiveDepot(depotId);
    try {
      document.dispatchEvent(new CustomEvent('hst-switch-depot-view', { detail: { depotId } }));
    } catch (_) { /* ignore in SSR */ }
    setTimeout(() => {
      document.getElementById('depot-management')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  if (depotPoints.length === 0) return null;

  return (
    <div
      className="bg-ink-800 border border-ink-600 rounded-xl overflow-hidden"
      data-testid="depot-network-map"
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-ink-700">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <Radar className="w-4 h-4 text-accent" /> {t('depot_map_title')}
        </h3>
        <div className="flex items-center gap-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: DEPOT_COLORS.hq }} /> {t('depot_map_legend_hq')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: DEPOT_COLORS.opened }} /> {t('depot_map_legend_opened')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: DEPOT_COLORS.acquired }} /> {t('depot_map_legend_acquired')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rotate-45 border" style={{ borderColor: PROSPECT_COLOR }} /> {t('depot_map_legend_prospect')}</span>
          <span className="flex items-center gap-1"><Crosshair className="w-2.5 h-2.5 text-rose-400" /> {t('depot_map_legend_target')}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3">
        {/* ── Carte ── */}
        <div className="lg:col-span-2 relative" style={{ background: '#0B1220' }}>
          <ComposableMap
            projection={projection}
            width={W}
            height={H}
            style={{ width: '100%', height: 'auto', background: '#0B1220' }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) => geographies.map(geo => {
                const isFR = geo.properties.name === 'France';
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { fill: isFR ? '#16264A' : '#101A30', stroke: isFR ? '#2D4A8A' : '#1A233A', strokeWidth: 0.5, outline: 'none' },
                      hover: { fill: isFR ? '#1A2D58' : '#101A30', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              })}
            </Geographies>

            {/* Rayon d'action du dépôt sélectionné */}
            {selection.type === 'depot' && radiusCircle && selected && (
              <Marker coordinates={radiusCircle.center}>
                <circle
                  r={radiusCircle.px}
                  fill={`${DEPOT_COLORS[depotKind(selected)]}14`}
                  stroke={DEPOT_COLORS[depotKind(selected)]}
                  strokeWidth={1.2}
                  strokeDasharray="6 4"
                  data-testid="depot-radius-circle"
                />
              </Marker>
            )}

            {/* Aperçu prospection : rayon 250 km de la ville survolée/choisie */}
            {previewCircle && (
              <Marker coordinates={previewCircle.center}>
                <circle
                  r={previewCircle.px}
                  fill={`${PROSPECT_COLOR}10`}
                  stroke={PROSPECT_COLOR}
                  strokeWidth={1}
                  strokeDasharray="3 4"
                  data-testid="depot-prospect-radius-circle"
                />
              </Marker>
            )}

            {/* Villes libres — prospection (survol = aperçu, clic = panneau) */}
            {candidates.map(({ city, status }) => {
              const dim = status !== 'open';
              const isHover = hoverCityId === city.id;
              const isSel = selection.type === 'prospect' && selection.id === city.id;
              return (
                <Marker
                  key={`cand-${city.id}`}
                  coordinates={[city.lng, city.lat]}
                  onClick={() => { setSelection({ type: 'prospect', id: city.id }); }}
                  onMouseEnter={() => setHoverCityId(city.id)}
                  onMouseLeave={() => setHoverCityId(null)}
                  style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' }, pressed: { cursor: 'pointer' } }}
                >
                  <g data-testid={`depot-map-prospect-${city.id}`} style={{ cursor: 'pointer' }} opacity={dim ? 0.45 : 1}>
                    <rect
                      x={-3.2} y={-3.2} width={6.4} height={6.4}
                      transform="rotate(45)"
                      fill={isSel || isHover ? `${PROSPECT_COLOR}40` : '#0B1220'}
                      stroke={PROSPECT_COLOR}
                      strokeWidth={isSel ? 1.8 : 1}
                    />
                    {(isHover || isSel) && (
                      <text textAnchor="middle" y={-8} style={{ fontSize: 7.5, fontWeight: 700, fill: PROSPECT_COLOR, fontFamily: 'monospace' }}>
                        {city.name}{isHover && hoverStats ? ` · +${hoverStats.unlocked}` : ''}
                      </text>
                    )}
                  </g>
                </Marker>
              );
            })}

            {/* Origines des contrats disponibles */}
            {contractDots.map(dot => (
              <Marker key={`ct-${dot.city.id}`} coordinates={[dot.city.lng, dot.city.lat]}>
                <circle r={3 + Math.min(3, dot.count)} fill={dot.covered ? '#34D39955' : '#F59E0B55'} stroke={dot.covered ? '#34D399' : '#F59E0B'} strokeWidth={0.8} style={{ pointerEvents: 'none' }} />
                <text textAnchor="middle" y={-7} style={{ fontSize: 7, fill: '#94A3B8', fontFamily: 'monospace', pointerEvents: 'none' }}>
                  {dot.count}
                </text>
              </Marker>
            ))}

            {/* Cibles M&A (concurrents non rachetés) */}
            {targets.map(tg => (
              <Marker key={`tgt-${tg.competitor.id}`} coordinates={[tg.city.lng, tg.city.lat]}>
                <g data-testid={`depot-map-competitor-${tg.competitor.id}`} style={{ pointerEvents: 'none' }}>
                  <circle r={7} fill="none" stroke="#FB7185" strokeWidth={1} strokeDasharray="2 2" />
                  <circle r={2} fill="#FB7185" />
                  <text textAnchor="middle" y={16} style={{ fontSize: 7.5, fill: '#FB7185', fontFamily: 'monospace' }}>
                    🎯 {tg.competitor.name}
                  </text>
                </g>
              </Marker>
            ))}

            {/* Dépôts possédés */}
            {depotPoints.map(({ depot, city }) => {
              const kind = depotKind(depot);
              const isSel = selection.type === 'depot' && selected && depot.id === selected.id;
              return (
                <Marker
                  key={depot.id}
                  coordinates={[city.lng, city.lat]}
                  onClick={() => setSelectedId(depot.id)}
                  style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' }, pressed: { cursor: 'pointer' } }}
                >
                  <g data-testid={`depot-map-marker-${depot.id}`} style={{ cursor: 'pointer' }}>
                    <circle r={isSel ? 9 : 7} fill="#0B1220" stroke={DEPOT_COLORS[kind]} strokeWidth={isSel ? 2.5 : 1.5} />
                    {kind === 'hq'
                      ? <text textAnchor="middle" y={3} style={{ fontSize: 9, fill: DEPOT_COLORS[kind] }}>★</text>
                      : <rect x={-3} y={-3} width={6} height={6} fill={DEPOT_COLORS[kind]} rx={1} />}
                    <text textAnchor="middle" y={isSel ? -13 : -11} style={{ fontSize: 8.5, fontWeight: 700, fill: '#E2E8F0', fontFamily: 'monospace' }}>
                      {city.name}
                    </text>
                  </g>
                </Marker>
              );
            })}
          </ComposableMap>
          <p className="absolute bottom-1.5 left-3 text-[9px] text-slate-500 font-mono pointer-events-none">
            {t('depot_map_prospect_hint')}
          </p>
        </div>

        {/* ── Panneau latéral : prospection OU dépôt sélectionné ── */}
        {selection.type === 'prospect' && prospectStats ? (
          <ProspectPanel
            stats={prospectStats}
            onOpenDepot={() => setModalCityId(prospectStats.city.id)}
          />
        ) : selected && panelStats ? (
          <div className="border-t lg:border-t-0 lg:border-l border-ink-700 p-4 space-y-3" data-testid="depot-map-panel">
            <div>
              <p className="text-[9px] uppercase tracking-wider font-mono" style={{ color: DEPOT_COLORS[depotKind(selected)] }}>
                {depotKind(selected) === 'hq' ? t('depot_map_kind_hq') : depotKind(selected) === 'acquired' ? t('depot_map_kind_acquired') : t('depot_map_kind_opened')}
              </p>
              <h4 className="text-base font-bold text-foreground flex items-center gap-2">
                {depotKind(selected) === 'hq' ? <Star className="w-4 h-4 text-amber-400" /> : <Building2 className="w-4 h-4 text-slate-400" />}
                {selected.name}
              </h4>
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" /> {panelStats.city?.name}{panelStats.city?.country !== 'FR' ? ` (${panelStats.city?.country})` : ''}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 text-center">
              {[
                { label: t('depot_map_stat_radius'), value: `${panelStats.radiusKm} km`, tid: 'radius' },
                { label: t('depot_map_stat_rent'), value: panelStats.rent > 0 ? `${formatMoney(panelStats.rent)}/m` : t('depot_map_rent_free'), tid: 'rent' },
                { label: t('depot_map_stat_vehicles'), value: `${panelStats.vCount}/${selected.capacityVehicles || 10}`, tid: 'vehicles' },
                { label: t('depot_map_stat_contracts'), value: panelStats.served, tid: 'contracts' },
              ].map(s => (
                <div key={s.tid} className="bg-ink-900/60 border border-ink-700 rounded-lg px-2 py-1.5" data-testid={`depot-map-stat-${s.tid}`}>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{s.label}</p>
                  <p className="text-sm font-bold font-mono text-foreground">{s.value}</p>
                </div>
              ))}
            </div>

            {/* 11/02/2026 — Retour user prod : la carte stratégique ne doit
                PAS afficher les évolutions possibles ; elles sont gérées
                individuellement dans chaque onglet dépôt (bouton « Gérer »
                ci-dessous). Ça allège aussi la lecture de la carte. */}

            <button
              onClick={() => handleManage(selected.id)}
              className="w-full text-xs font-bold py-2 rounded-lg bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 transition-colors"
              data-testid="depot-map-manage-btn"
            >
              {t('depot_map_manage_cta')}
            </button>
          </div>
        ) : null}
      </div>

      <NewDepotModal
        open={!!modalCityId}
        onClose={() => setModalCityId(null)}
        initialCityId={modalCityId}
      />
    </div>
  );
}
